import { OpsFeedsSearchModel } from '../list/search/feeds_search_types';
import { HttpErrorIgnore } from '../../../../global';
import apiSvc from '../../../core/services/apiSvc';
import utilSvc from '../../../core/utilSvc';
import validSvc from '../../../core/validSvc';
import { FeedsDataItem } from '../list/feeds_list_types';
import {
    ALL_SELECTED_LABEL_1,
    ALL_SELECTED_LABEL_2,
    ALL_SELECTED_LABEL_3,
} from '../../../core/components/validation/ValidInputMultiSelect';
import { getUiModeForFeeds } from '../list/pagehooks/useFeedsInit';

const GET_DETAIL = 'ops/feeds/get';
const GET_DETAIL_CE = 'ops/datamonitor/activity/get';
export const GET_LIST = 'ops/feeds/list';
export const GET_LIST_CE = 'ops/datamonitor/activity/list';
const REVERSE_FILE = 'ops/splittrans/reversefile';
const PROCESS_FILE = 'ops/feeds/processfile';
const ARCHIVE_FILE = 'ops/feeds/archivefile';
const RELOAD_FILE = 'ops/feeds/reset';

const formatAllSelected = (val: string[]): string[] => {
    if (
        val.length === 1 &&
        (val[0] === ALL_SELECTED_LABEL_1 || val[0] === ALL_SELECTED_LABEL_2 || val[0] === ALL_SELECTED_LABEL_3)
    ) {
        return [];
    }
    return val;
};

async function getDetail(id: string, isCE: boolean): Promise<FeedsDataItem> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('feeds.repo.getDetail, id is required');
    }

    const res = apiSvc.getEntity(isCE ? GET_DETAIL_CE : GET_DETAIL, {
        Params: {
            Id: id,
        },
    }) as unknown as FeedsDataItem;
    // Sort Pharmacy array alphabetically
    if (Array.isArray(res.Pharmacy)) {
        res.Pharmacy = utilSvc.array.sortTextASC(res.Pharmacy, undefined);
    }
    if (Array.isArray(res.CoveredEntity)) {
        res.CoveredEntity = utilSvc.array.sortTextASC(res.CoveredEntity, undefined);
    }
    return res;
}

type ReverseFileParams = {
    id: string;
    resetFileHash: boolean;
    resetRecordHash: boolean;
    notes: string;
};

async function reverseFile(params: ReverseFileParams): Promise<void> {
    // params
    if (!params || !validSvc.isText(params.id)) {
        throw new Error('feeds.repo.reverseFile, id is required');
    }

    const apiRequest = {
        Params: {
            Id: params.id,
            ResetFileHash: params.resetFileHash,
            ResetRecordHash: params.resetRecordHash,
            Notes: params.notes,
        },
    };

    await apiSvc.post(REVERSE_FILE, apiRequest);
}

async function processFile(id: string): Promise<void> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('feeds.repo.processFile, id is required');
    }

    const apiRequest = {
        Entity: {
            Params: {
                Id: id,
            },
        },
    };

    await apiSvc.post(PROCESS_FILE, apiRequest);
}

async function archiveFile(id: string): Promise<void> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('feeds.repo.archiveFile, id is required');
    }

    const apiRequest = {
        Entity: {
            Params: {
                Id: id,
            },
        },
    };

    await apiSvc.post(ARCHIVE_FILE, apiRequest);
}

async function reloadFile(id: string): Promise<void> {
    // params
    if (!validSvc.isText(id)) {
        throw new Error('feeds.repo.reloadFile, id is required');
    }

    const apiRequest = {
        Entity: {
            Params: {
                Id: id,
            },
        },
    };

    await apiSvc.post(RELOAD_FILE, apiRequest);
}

export async function getList(query: OpsFeedsSearchModel, isCE: boolean): Promise<FeedsDataItem[] | HttpErrorIgnore> {
    const userType = getUiModeForFeeds();

    try {
        const sourceOpts = getStatusOptions();
        const statusOpts = getStatusOptions();
        const feedTypeOpts = getFeedTypeOptions(userType);
        const params = {
            EndDate: utilSvc.dt.toIsoDate(query.EndDate.val),
            StartDate: utilSvc.dt.toIsoDate(query.StartDate.val),
            FeedType: query.FeedType.val.length === feedTypeOpts.length ? [] : formatAllSelected(query.FeedType.val),
            ObjectState: query.Status.val.length === statusOpts.length ? [] : formatAllSelected(query.Status.val),
            Source: query.Source.val.length === sourceOpts.length ? [] : formatAllSelected(query.Source.val),
        };

        const response = await apiSvc.getEntityList<FeedsDataItem>(isCE ? GET_LIST_CE : GET_LIST, {
            Context: {
                CEID: formatAllSelected(query.CE.val) || [],
                RXID: formatAllSelected(query.RX.val) || [],
            },
            Paging: {
                Page: 1,
                Size: 20000,
            },
            Params: isCE
                ? {
                      ...params,
                      ObjectState: [],
                      Source: [],
                  }
                : params,
            SortBy: [{ Field: 'ImportDate', Order: 'DSC' }],
        });
        return response;
    } catch (err: any) {
        // all entity list errors handled upstream
        return utilSvc.http.ignore(err, 'feedsRepo.getList') as HttpErrorIgnore;
    }
}

// Generate query object for feeds search, modeled after providerRepo.generateQuery
export function generateFeedsQuery(formState: OpsFeedsSearchModel): Record<string, any> {
    const q: Record<string, any> = {
        CEID: formState.CE.val || [],
        RXID: formState.RX.val || [],
    };

    if (formState.FeedType.val && formState.FeedType.val.length > 0) {
        q.FeedType = formState.FeedType.val;
    }

    if (formState.Status.val && formState.Status.val.length > 0) {
        q.ObjectState = formState.Status.val;
    }

    if (formState.Source.val && formState.Source.val.length > 0) {
        q.Source = formState.Source.val;
    }

    if (formState.StartDate.val) {
        q.StartDate = formState.StartDate.val;
    }

    if (formState.EndDate.val) {
        q.EndDate = formState.EndDate.val;
    }

    if (formState.DateRangeMode.val) {
        q.DateRangeMode = formState.DateRangeMode.val;
    }

    return q;
}

/**
 * Get feed type options for the search form
 * Supports multiple UI modes:
 * - 'VERITY': Returns all possible feed types (~90+ options)
 * - 'CDC': Returns base list plus CDC_RESTOCK
 * - 'CE': Returns base list (or filtered list for VeriSaveOnly mode)
 * Defaults to 'VERITY' mode if not specified
 */
export function getFeedTypeOptions(uiMode: string): string[] {
    const baseList = [
        'ADT',
        'CARDINAL_ASSIST_IN',
        'CARDINAL_ASSIST_OUT',
        'CE_LOCATIONS',
        'CENPI',
        'CEREVIEWNPI',
        'EDI',
        'EDI810',
        'EDI832',
        'EDI845',
        'EDI846',
        'EDI850',
        'EDI855',
        'EDI997',
        'EPRESCRIBE',
        'MATCH_BULK_ACTION',
        'ORDER',
        'PATIENTVISITS',
        'REFERRAL',
        'SPLITTRANS',
    ];

    const verityExtras = [
        'ACCUMULATORS',
        'ASTRANSFORMS',
        'AUDITZIP',
        'CDC_RESTOCK',
        'CHANGEHEALTH_PAYORS',
        'CVS_DISPENSE_DISQUALIFIED',
        'CVS_DISPENSE_QUALIFIED',
        'CVS_DISPENSE_RECEIVE',
        'CVS_FORMULARY',
        'CUSTOMPRICE',
        'DISPENSES',
        'DISPENSE_FEE_LIST',
        'DISPENSE_POOL',
        'ESI_CLAIMS',
        'ESI_CLAIMS_OUT',
        'EXTRACT_CASHCARD_MEMBER',
        'EXTRACT_MATCHEDDISPENSE',
        'EXTRACT_MEDICAID_CLAIMS',
        'EXTRACT_MED_REBATECLAIMS',
        'EXTRACT_REBATECLAIMS',
        'EXTRACT_NPI',
        'EXTRACT_NPI_MASTER',
        'EXTRACT_NPI_OMNICELL',
        'EXTRACT_PBMNPI',
        'EXTRACT_PBM_PRICES',
        'EXTRACT_WNINVOICE',
        'EXTRACT_WNGL',
        'EXTRACT_ORDER_LIST',
        'EXTRACT_INVOICE_LIST',
        'EXTRACT_CEMATCHES',
        'EXTRACT_CEACCUMULATIONS',
        'EXTRACT_CEAUDIT',
        'EXTRACT_SUSPECTS',
        'EXTRACT_CECLAIMSBACKING',
        'EXTRACT_CE_SPLITMATCH_CLAIMSBACKING',
        'EXTRACT_ORDERINSITE_ACCUMS',
        'EXTRACT_ORDERINSITE_CEMAP',
        'EXTRACT_RX_INVOICED_CLAIMS',
        'EXTRACT_PHMATCHES',
        'EXTRACT_RX_INVOICED_CLAIMS_OTF',
        'FINANCE_CORRECTIONS',
        'FINANCE_INVOICES',
        'FINANCE_TPA_ROLLUPS',
        'FLATFILE_INVOICE',
        'INVOICE_ADJUSTMENT',
        'INVOICE_PRESCRIPTION',
        'MANUAL_CATALOGS',
        'MATCH_FEE_EXPORT',
        'MIGRATION_ACCUMULATOR',
        'MIGRATION_TRANSFORM',
        'NPIREGISTRY',
        'PACKAGEDITEMS',
        'PACKAGEDITEMSDESC',
        'PAYORS_EXT',
        'PRECISION_BRIDGE',
        'PRECISION_PLAN',
        'PREQUALIFIED_DISP',
        'PRICECATALOG',
        'PUNCHOUT',
        'PURCHASE_HISTORY',
        'PURGE',
        'TPADISPENSES',
        'TRANSFORMS',
        'VHUB_ACCUM',
        'VHUB_DQ_CLAIMS',
        'VHUB_FEDERATED_832',
        'VHUB_FEDERATED_810',
        'VHUB_FEDERATED_CLAIMS',
        'VHUB_CAREPATH_FEDERATED_CLAIMS',
        'VHUBINVOICED',
        'VISIT_REVERSAL',
        'DOD_PRICES',
        'DOD_INVOICED',
        'ACCUM_OWN',
        'EDI527',
        'TPANPIS',
        'EXTRACT_CE_CPINVOICES',
        'EXTRACT_CE_SPLITMATCH_CLAIMSUNMATCHED',
        'EXTRACT_CESPLITBILLING_ACCUMDISPENSES',
        'EXTRACT_CESPLITBILLING_INVOICEDDISPENSES',
        'EXTRACT_EXT_ACCUMS',
        'FEDERATED_850',
        'EXTRACT_KY_MEDICAID_CLAIMS_ERRORS',
    ];

    let result: string[] = [];

    switch (uiMode) {
        case 'CDC':
            // CDC mode: base list + CDC_RESTOCK
            result = [...baseList, 'CDC_RESTOCK'];
            break;
        case 'CE':
            // CE mode: base list (VeriSaveOnly filter handled by parent if needed)
            result = baseList;
            break;
        case 'VERITY':
        default:
            // VERITY mode: all possible feed types
            result = [...baseList, ...verityExtras];
            break;
    }

    result.sort();
    return result;
}

/**
 * Get status options for the search form
 * Returns all possible status values available across all UI modes
 * These are consistent across VERITY, CDC, and CE modes
 */
export function getStatusOptions(): string[] {
    return [
        'ARCHIVED',
        'DUPLICATE',
        'ERROR',
        'PROCESSED',
        'RAW',
        'READY',
        'RECEIVED',
        'REJECTED',
        'REVERSED',
        'SKIPPED',
        'TRANSFERRING',
        'WARNING',
    ];
}

/**
 * Get source options for the search form
 * Returns all possible data source partners
 * These are consistent across VERITY, CDC, and CE modes
 */
export function getSourceOptions(): string[] {
    return [
        '340BDIRECT',
        'ABCSAFECOR',
        'ADT',
        'ALTSCRIPTS',
        'AMERISOURCE',
        'BAXTEREDI',
        'BIOLOGICSROLLUP',
        'CAPTURERX',
        'CAREPATHRX',
        'CARDINAL',
        'CARDINALPR',
        'CARDINALSAFECOR',
        'CDC',
        'CURASCRIPTEDI',
        'DAKOTADRUG',
        'DCS',
        'Emdeon',
        'FFF',
        'HUDSONHEADWATERS',
        'KEYSOURCE',
        'MACROHELIX',
        'MCKESSON',
        'MCKESSONHCP',
        'MCKESSONSAFECOR',
        'MEDISPAN',
        'MORRISDICKSON',
        'MPB',
        'OMNICELL',
        'OWENSMINOREDI',
        'POWERLINE',
        'PRECISION',
        'PROCARE',
        'Relay',
        'ROCHESTERDRUG',
        'SMITHDRUG',
        'SPD',
        'SUNRX',
        'TRULLA',
    ];
}

export default {
    getList,
    getDetail,
    reverseFile,
    processFile,
    archiveFile,
    reloadFile,
    getFeedTypeOptions,
    getStatusOptions,
    getSourceOptions,
};
